import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LeftArrowNext } from '@tv2/ui-system/icons/next/navigation/LeftArrowNext';
import { RightArrowNext } from '@tv2/ui-system/icons/next/navigation/RightArrowNext';
import { ScrollRowItem } from './ScrollRowItem';

export function ScrollRow<T extends ScrollRowItem>({
  cards,
  renderCard,
  onChange,
  startOffset,
  className = '',
  onScrolledToActiveNode,
}: Readonly<{
  cards: T[];
  renderCard: (card: T) => React.ReactNode;
  onChange?: (current: T, isControlled: boolean) => void;
  startOffset?: number;
  className?: string;
  onScrolledToActiveNode?: (node: HTMLDivElement) => void;
}>) {
  const [visibleCards, setVisibleCards] = useState<Record<string, boolean>>({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const visibleCardList = useMemo(() => {
    return cards.filter(card => visibleCards[card.id]);
  }, [cards, visibleCards]);

  const firstVisibleCard = visibleCardList.at(0);
  const lastVisibleCard = visibleCardList.at(-1);
  const canScrollBack = firstVisibleCard !== cards.at(0);
  const canScrollForward = lastVisibleCard !== cards.at(-1);
  const startOffsetCard = startOffset === undefined ? undefined : cards.at(startOffset);
  const isControlled = useRef(startOffsetCard !== undefined);

  const observer = useMemo(() => {
    if (typeof IntersectionObserver !== 'undefined') {
      return new IntersectionObserver(
        entries => {
          setVisibleCards(prev => {
            return {
              ...prev,
              ...Object.fromEntries(
                entries.map(entry => [
                  (entry.target as HTMLDivElement).dataset.id,
                  entry.isIntersecting,
                ])
              ),
            };
          });
        },
        { threshold: 0.5, rootMargin: '0px 0px 500px 0px' }
      );
    }
  }, []);

  const nodes = useRef<Record<string, HTMLDivElement>>({});

  const nextPageActiveCard =
    nodes.current[
      cards[cards.findIndex(card => card.id === lastVisibleCard?.id) + 1]?.id ?? cards.at(-1)?.id
    ];
  const prevPageActiveCard =
    nodes.current[
      cards[cards.findIndex(card => card.id === firstVisibleCard?.id) - visibleCardList.length]
        ?.id ?? cards.at(0)?.id
    ];

  const registerNode = useCallback(
    (id: string, element: HTMLDivElement) => {
      observer?.observe(element);
      nodes.current[id] = element;
    },
    [observer]
  );

  const unregisterNode = useCallback(
    (id: string) => {
      if (nodes.current && nodes.current[id]) {
        observer?.unobserve(nodes.current[id]);
        delete nodes.current[id];
      }
    },
    [observer]
  );

  const scrollTo = useCallback((node: HTMLDivElement | undefined) => {
    if (scrollRef.current && node) {
      isControlled.current = false;
      const left = node.offsetLeft || 0;
      scrollRef.current.scrollTo({
        left,
        behavior: 'smooth',
      });
    }
  }, []);

  const handleLeftClick = useCallback(
    () => scrollTo(prevPageActiveCard),
    [scrollTo, prevPageActiveCard]
  );

  const handleRightClick = useCallback(
    () => scrollTo(nextPageActiveCard),
    [scrollTo, nextPageActiveCard]
  );

  const handleWheel = useCallback<EventListener>(e => {
    isControlled.current = false;
  }, []);

  useEffect(() => {
    return () => observer?.disconnect();
  }, [observer]);

  useEffect(() => {
    if (firstVisibleCard && onChange) onChange(firstVisibleCard, isControlled.current);
  }, [firstVisibleCard, onChange]);

  useEffect(() => {
    if (scrollRef.current && startOffsetCard) {
      const node = nodes.current[startOffsetCard.id];
      if (node) {
        isControlled.current = true;
        scrollRef.current.scrollTo({
          left: node.offsetLeft,
          behavior: 'instant',
        });
        onScrolledToActiveNode?.(node);
      }
    }
  }, [startOffsetCard, onScrolledToActiveNode]);

  useEffect(() => {
    if (scrollRef.current) {
      const currentScrollRef = scrollRef.current;
      currentScrollRef.addEventListener('wheel', handleWheel, {
        passive: false,
      });

      return () => {
        currentScrollRef.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleWheel]);

  return (
    <div className="relative">
      <ArrowButton
        hide={!canScrollBack}
        title="Forrige række"
        direction="left"
        onClick={handleLeftClick}
      />
      <div
        className={`flex flex-row relative snap-x snap-mandatory scroll-px-[var(--spacingLeft)] px-[var(--spacingLeft)] -mr-[var(--gap)] hide-scrollbar overflow-hidden overscroll-x-contain overflow-x-auto ${className}`}
        ref={scrollRef}
      >
        {cards.map(card => (
          <ScrollRowItem
            key={card.id}
            id={card.id}
            register={registerNode}
            unregister={unregisterNode}
          >
            {renderCard(card)}
          </ScrollRowItem>
        ))}
      </div>
      <ArrowButton
        hide={!canScrollForward}
        title="Næste række"
        direction="right"
        onClick={handleRightClick}
      />
    </div>
  );
}

const ArrowButton = memo(
  ({
    direction,
    title,
    onClick,
    hide,
  }: {
    direction: 'left' | 'right';
    title: string;
    onClick: () => void;
    hide: boolean;
  }) => {
    return (
      <div
        className={`z-[1] flex items-center justify-center absolute top-0 h-full max-md:hidden ${
          direction === 'left' ? 'left-0' : 'right-0'
        }${hide ? ' hidden' : ''}`}
      >
        <button type="button" title={title} onClick={onClick} className="flex group h-fit">
          <span className="flex m-2 w-12 h-12 items-center justify-center rounded-full bg-midnight/60 group-hover:bg-grey-100 duration-200">
            {direction === 'left' ? (
              <LeftArrowNext className="invert group-hover:invert-0" height={24} width={24} />
            ) : (
              <RightArrowNext className="invert group-hover:invert-0" height={24} width={24} />
            )}
          </span>
        </button>
      </div>
    );
  }
);
